import React, { useEffect, useState } from "react";
import { Check, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@pgs-core/ui/components/dialog";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import {
  createPostCategory,
  deletePostCategory,
  fetchPostCategories,
  renamePostCategory,
  type PostCategory,
} from "../lib/posts";
import type { ApiClient } from "../lib/api";

/**
 * Manage blog categories: create, rename (slug stays stable so live category
 * URLs don't break), and delete. Deleting only unlinks posts, never removes
 * them.
 */
export function PostCategoriesDialog({ open, onOpenChange, api, onChanged }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  api: ApiClient;
  onChanged: () => void;
}) {
  const [categories, setCategories] = useState<PostCategory[] | null>(null);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  useEffect(() => {
    if (!open || !api) return;
    setError("");
    fetchPostCategories(api)
      .then(setCategories)
      .catch((err: Error) => setError(err.message));
  }, [open, api]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await action();
      setCategories(await fetchPostCategories(api));
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const create = () => run(async () => {
    await createPostCategory(api, newName.trim());
    setNewName("");
  });

  const rename = () => run(async () => {
    if (editingId) await renamePostCategory(api, editingId, editingName.trim());
    setEditingId(null);
  });

  const remove = (category: PostCategory) => {
    const linked = category.post_count ?? 0;
    const warning = linked > 0
      ? `Delete "${category.name}"? It will be removed from ${linked} post${linked === 1 ? "" : "s"} (the posts stay).`
      : `Delete "${category.name}"?`;
    if (!window.confirm(warning)) return;
    void run(() => deletePostCategory(api, category.id));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Blog categories</DialogTitle>
          <DialogDescription>
            Group related posts. Each category gets its own page at /blog/category/…
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {!categories && !error && (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {categories?.length === 0 && (
            <p className="py-2 text-sm text-muted-foreground">No categories yet.</p>
          )}
          {categories?.map((category) => (
            <div key={category.id} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
              {editingId === category.id ? (
                <>
                  <Input
                    value={editingName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingName(e.target.value)}
                    className="h-7"
                    autoFocus
                  />
                  <Button variant="ghost" size="sm" disabled={busy || !editingName.trim()} onClick={rename} aria-label="Save name">
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => setEditingId(null)} aria-label="Cancel rename">
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <span className="flex-1 font-medium">{category.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {category.post_count ?? 0} post{(category.post_count ?? 0) === 1 ? "" : "s"}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => { setEditingId(category.id); setEditingName(category.name); }}
                    aria-label={`Rename ${category.name}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => remove(category)}
                    aria-label={`Delete ${category.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          ))}

          <div className="flex items-center gap-2 pt-1">
            <Input
              value={newName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
              placeholder="New category name"
              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter" && newName.trim()) create(); }}
            />
            <Button size="sm" disabled={busy || !newName.trim()} onClick={create}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add
            </Button>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
